import { Component, inject, OnInit, signal } from '@angular/core';
import { DesyAngularModule } from 'desy-angular';
import { ActivatedRoute, Router } from '@angular/router';
import { DeploySystemOfIaStep1Component } from './step1/deploy-system-of-ia-step-1.component';
import { DeploySystemOfIaStep2Component } from './step2/deploy-system-of-ia-step-2.component';
import { DeploySystemOfIaStep3Component } from './step3/deploy-system-of-ia-step-3.component';
import { DeploySystemOfIaStep4Component } from './step4/deploy-system-of-ia-step-4.component';
import { DeploySystemOfIaStep5Component } from './step5/deploy-system-of-ia-step-5.component';
import { DeploySystemOfIaStep6Component } from './step6/deploy-system-of-ia-step-6.component';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { SystemsService } from '@/app/services/ia-services/systems.service';
import {
  AiSystemDeployment,
  Step1Deployment,
  Step2Deployment,
  Step3Deployment,
  Step4Deployment,
  Step5Deployment,
  Step6Deployment,
} from '@/app/interfaces/aiSystemDeployment';

@Component({
  selector: 'app-deploy-system-of-ia',
  templateUrl: './deploy-system-of-ia.component.html',
  styleUrls: ['./deploy-system-of-ia.component.scss'],
  imports: [
    ConfirmDialog,
    DesyAngularModule,
    DeploySystemOfIaStep1Component,
    DeploySystemOfIaStep2Component,
    DeploySystemOfIaStep3Component,
    DeploySystemOfIaStep4Component,
    DeploySystemOfIaStep5Component,
    DeploySystemOfIaStep6Component,
  ],
  providers: [ConfirmationService],
})
export class DeploySystemOfIaComponent implements OnInit {
  private systemsService = inject(SystemsService);
  private userData = JSON.parse(localStorage.getItem('userData') || '{}') as { orgCode?: string };
  id_system: string = '';
  constructor(
    private router: Router,
    private route: ActivatedRoute
  ) {}

  currentView = signal(1);

  newDeployment = signal<AiSystemDeployment>({
    systemName: '',
    systemCode: '',
    systemClassification: '',
    biases: null,
    risks: null,
    usageInstructions: null,
    humanSupervision: null,
    inputDataQuality: null,
    qualityMonitoring: null,
    logRetention: null,
    fria: null,
    dpia: null,
    naturalPersonInteraction: null,
    contentGeneration: null,
    biometricsEmotions: null,
    publicInterestText: null,
    userInformation: null,
    aiInfrastructure: null,
    integrationMode: null,
    dataSource: null,
    timePeriod: null,
    dataCategory: null,
    qualityAccreditation: null,
    inputDataSource: null,
    sourceCode: null,
    iso42001: null,
    iso42001Csv: null,
    iso42001Description: null,
    dataSovereignty: null,
    dataSovereigntyDescription: null,
    equityAndAccess: '',
    equityAndAccessDescription: null,
    aiSystemUpdate: null,
    aiSystemUpdateDescription: null,
    latencyPerformance: null,
    latencyPerformanceDescription: null,
    scalability: null,
    scalabilityCsv: null,
    contingencyPlan: null,
    contingencyPlanCsv: null,
    cybersecurity: null,
    cybersecurityCsv: null,
    cybersecurityDescription: null,
    accessControl: null,
    accessControlDescription: null,
    attackProtection: null,
    attackProtectionCsv: null,
    attackProtectionDescription: null,
    resilience: null,
    resilienceCsv: null,
    resilienceDescription: null,
    costBenefitAnalysis: null,
    costBenefitAnalysisCsv: null,
    costBenefitAnalysisDescription: null,
    publicValue: null,
    publicValueDescription: null,
  });

  async ngOnInit() {
    this.id_system = this.route.snapshot.paramMap.get('id') ?? '';
    const data = await this.systemsService.getSystemDetails(this.id_system, 'system');
    this.newDeployment.update((current) => ({
      ...current,
      systemName: data.systemName,
      systemClassification: data.systemClassification,
      systemCode: this.id_system,
    }));
  }

  private updateDeployment(stepData?: Partial<AiSystemDeployment> | null) {
    if (!stepData) {
      return;
    }

    this.newDeployment.update((current) => ({
      ...current,
      ...stepData,
    }));
  }

  private updateDeploymentAndGoToStep(stepData: Partial<AiSystemDeployment> | null | undefined, targetStep: number) {
    this.updateDeployment(stepData);
    this.currentView.set(targetStep);
  }

  onStep1Completed(step1Data: Step1Deployment) {
    this.updateDeploymentAndGoToStep(step1Data, 2);
  }

  onStep2Completed(step2Data: Step2Deployment) {
    this.updateDeploymentAndGoToStep(step2Data, 3);
  }

  onStep3Completed(step3Data: Step3Deployment) {
    this.updateDeploymentAndGoToStep(step3Data, 4);
  }

  onStep4Completed(step4Data: Step4Deployment) {
    this.updateDeploymentAndGoToStep(step4Data, 5);
  }

  onStep5Completed(step5Data: Step5Deployment) {
    this.updateDeploymentAndGoToStep(step5Data, 6);
  }

  async onStep6Completed(step6Data: Step6Deployment) {
    this.updateDeployment(step6Data);

    try {
      await this.systemsService.createDeployment({
        ...this.newDeployment(),
        orgCode: this.userData.orgCode,
      });
      this.router.navigate([`/catalogo-ia/sistemas-de-ia/listado-sistemas`]); // En el futuro, hacer que vaya al detalle del sistema desplegado
    } catch (error) {
      console.error('Error creating deployment:', error);
    }
  }

  goToPreviousStepFromStep2(step2Data?: Step2Deployment | null) {
    this.updateDeploymentAndGoToStep(step2Data, 1);
  }

  goToPreviousStepFromStep3(step3Data?: Step3Deployment | null) {
    this.updateDeploymentAndGoToStep(step3Data, 2);
  }

  goToPreviousStepFromStep4(step4Data?: Step4Deployment | null) {
    this.updateDeploymentAndGoToStep(step4Data, 3);
  }

  goToPreviousStepFromStep5(step5Data?: Step5Deployment | null) {
    this.updateDeploymentAndGoToStep(step5Data, 4);
  }

  goToPreviousStepFromStep6(step6Data?: Step6Deployment | null) {
    this.updateDeploymentAndGoToStep(step6Data, 5);
  }
}
